// Autosave: Debounce 1,5 s nach letzter Änderung, spätestens alle 10 s.
// Bei pagehide/visibilitychange Flush mit keepalive (Safari killt Tabs aggressiv).

import { savePending, clearPending } from "./offline_store"

const DEBOUNCE_MS = 1500
const MAX_WAIT_MS = 10000

export default class SceneSync {
  constructor(doc, { url, csrfToken, onStatus, beforeFlush }) {
    this.doc = doc
    this.url = url
    this.csrfToken = csrfToken
    this.onStatus = onStatus || (() => {})
    this.beforeFlush = beforeFlush || (() => {})
    this.version = null
    this.dirty = false
    this.inFlight = false
    this.conflicted = false

    this.flushBound = () => { if (this.dirty) this.flush({ keepalive: true }) }
    this.visibilityBound = () => {
      if (document.visibilityState === "hidden") this.flushBound()
    }
    addEventListener("pagehide", this.flushBound)
    document.addEventListener("visibilitychange", this.visibilityBound)
    // Turbo tauscht den Body ohne pagehide — sonst gehen Änderungen beim
    // Klick auf Stückliste/Materialien oder beim Umbenennen verloren
    document.addEventListener("turbo:before-visit", this.flushBound)
    // Werkstatt-WLAN kommt und geht: offline sofort anzeigen, online nachspülen
    this.offlineBound = () => this.onStatus("offline")
    this.onlineBound = () => this.flushBound()
    addEventListener("offline", this.offlineBound)
    addEventListener("online", this.onlineBound)
  }

  dispose() {
    clearTimeout(this.debounceTimer)
    clearTimeout(this.maxWaitTimer)
    removeEventListener("pagehide", this.flushBound)
    document.removeEventListener("visibilitychange", this.visibilityBound)
    document.removeEventListener("turbo:before-visit", this.flushBound)
    removeEventListener("offline", this.offlineBound)
    removeEventListener("online", this.onlineBound)
  }

  start(version) {
    this.version = version
  }

  // Fremder Stand wurde nachgezogen: Basis-Version übernehmen, damit der
  // nächste PATCH wieder passt und nicht im 409 endet. Die lokale Kopie im
  // Offline-Speicher fliegt mit weg — sie beschreibt eine Version, die es auf
  // dem Server nicht mehr gibt, und würde beim nächsten Seitenaufruf sonst
  // fälschlich als „neuer“ gelten.
  //
  // Nur aufrufen, wenn nichts Ungespeichertes offen ist (siehe collabAction);
  // sonst verschwänden fremde und eigene Arbeit gleichzeitig.
  adoptVersion(version) {
    this.version = version
    clearPending(this.url)
  }

  markDirty() {
    if (this.conflicted) return
    this.dirty = true
    savePending(this.url, { version: this.version, payload: this.doc.toJSON() })
    this.onStatus("dirty")
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.flush(), DEBOUNCE_MS)
    if (!this.maxWaitTimer) {
      this.maxWaitTimer = setTimeout(() => this.flush(), MAX_WAIT_MS)
    }
  }

  async flush({ keepalive = false } = {}) {
    clearTimeout(this.debounceTimer)
    clearTimeout(this.maxWaitTimer)
    this.maxWaitTimer = null
    if (!this.dirty || this.inFlight || this.conflicted) return

    this.dirty = false
    this.inFlight = true
    this.onStatus("saving")
    this.beforeFlush()

    try {
      const response = await fetch(this.url, {
        method: "PATCH",
        keepalive,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ base_version: this.version, ...this.doc.toJSON() })
      })

      if (response.status === 409) {
        this.conflicted = true
        this.onStatus("conflict")
        return
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const body = await response.json()
      this.version = body.version
      clearPending(this.url)
      this.onStatus("saved", new Date(body.saved_at))
      if (this.dirty) this.markDirty()
    } catch (error) {
      this.dirty = true
      savePending(this.url, { version: this.version, payload: this.doc.toJSON() })
      // fetch wirft TypeError, wenn gar keine Antwort kam (kein Netz) —
      // HTTP-Fehler mit Response bleiben "error"
      const offline = error instanceof TypeError || navigator.onLine === false
      this.onStatus(offline ? "offline" : "error", error)
      this.debounceTimer = setTimeout(() => this.flush(), 5000)
    } finally {
      this.inFlight = false
    }
  }
}
